'use memo';

import {
  AgxBodyText,
  AgxButton,
  AgxColumn,
  AgxHeader,
  AgxRow,
  Breakpoints,
  useWindowSize,
} from '@urbanx/agx-ui-components';
import dayjs from 'dayjs';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { mapPaymentStatus } from './MarketingPackagePaymentStatusMapper';
import FormDetailsHeader from '../../Common/FormDetailsHeader';
import { formatCurrency } from 'utils/formatNumber';
import { getMarketingPackageTotal } from 'utils/marketingPackageUtil';
import './MarketingPackageCompletePanel.scss';
import { MarketingPackageProjection } from '@urbanx/agx.marketing.client';

interface Props {
  onBack: () => void;
  marketingPackage: MarketingPackageProjection;
  addressLineOne: string;
  addressLineTwo: string;
}

const MarketingPackageCompletePanel = ({
  onBack,
  marketingPackage,
  addressLineOne,
  addressLineTwo,
}: Props) => {
  const lastUpdatedOn = `Last updated ${dayjs(marketingPackage.lastUpdated).format('DD/MM/YY')}`;

  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;

  const getFileLink = useGetFileLink();

  const downloadDocument = async () => {
    if (marketingPackage.document?.containerFilePath) {
      const fileLink = await getFileLink(
        marketingPackage.document?.containerFilePath
      );
      if (fileLink !== null) {
        openFileInNewTab(!isMobile, fileLink);
      }
    }
  };

  const marketingPackageTotal = getMarketingPackageTotal(marketingPackage);

  const marketingPaymentStatusComponent = (
    <div className="grid-wrapper">
      <AgxRow fill extraClasses="card-header" spaceBetween justifyCenter>
        {mapPaymentStatus(marketingPackage.displayStatus)}
      </AgxRow>
      {marketingPackage.displayStatusExtraInfo && (
        <AgxRow fill extraClasses="payment-status-row" justifyCenter>
          <AgxBodyText small extraClasses="payment-status-rowText">
            * {marketingPackage.displayStatusExtraInfo}
          </AgxBodyText>
        </AgxRow>
      )}
    </div>
  );

  const marketingInclusionsComponent = (
    <div className="grid-wrapper">
      <AgxRow fill extraClasses="card-header" spaceBetween>
        <AgxBodyText medium>Inclusions</AgxBodyText>
      </AgxRow>
      {marketingPackage?.inclusions?.map((inclusion, idx) => {
        return (
          <AgxRow key={`inclusion-${idx}`} fill extraClasses="inclusions-row">
            <AgxRow centered fill>
              <AgxBodyText small extraClasses="inclusions-rowText itemName">
                {inclusion.itemTemplateName}
              </AgxBodyText>
            </AgxRow>
            <AgxBodyText small extraClasses="inclusions-rowText itemPrice">
              {!inclusion.pricePerUnit
                ? 'Complimentary'
                : formatCurrency(inclusion.pricePerUnit)}
            </AgxBodyText>
          </AgxRow>
        );
      })}
    </div>
  );

  const marketingAddonsComponent = (
    <div className="grid-wrapper">
      <AgxRow fill extraClasses="card-header" spaceBetween>
        <AgxBodyText medium>Upgrades</AgxBodyText>
      </AgxRow>
      {marketingPackage?.addons?.map((addon, idx) => {
        return (
          <AgxRow key={`addon-${idx}`} fill extraClasses="addons-row">
            <AgxRow centered fill>
              <AgxBodyText small extraClasses="addons-rowText itemName">
                {addon.itemTemplateName}
              </AgxBodyText>
            </AgxRow>
            <AgxBodyText small extraClasses="addons-rowText itemPrice">
              {!addon.pricePerUnit
                ? 'Complimentary'
                : formatCurrency(addon.pricePerUnit)}
            </AgxBodyText>
          </AgxRow>
        );
      })}
    </div>
  );

  const noMarketingInclusionsComponent = (
    <div className="grid-wrapper">
      <AgxRow fill extraClasses="card-header" spaceBetween>
        <AgxBodyText small>Inclusions</AgxBodyText>
      </AgxRow>
      <AgxRow key={`inclusions-missing`} fill extraClasses="inclusions-row">
        <AgxRow centered fill>
          <AgxBodyText small extraClasses="addons-rowText itemName">
            Inclusions list not available. Download to view.
          </AgxBodyText>
        </AgxRow>
      </AgxRow>
    </div>
  );

  const marketingTotalComponent = (
    <div className="grid-wrapper">
      {!!marketingPackage.marketingContributions?.vendorUpfrontPayment &&
        (!!marketingPackage.marketingContributions
          ?.agentContributionNoReimbursement ||
          !!marketingPackage.marketingContributions
            ?.agentContributionToBeReimbursed) && (
          <AgxRow fill extraClasses="total-row" spaceBetween>
            <AgxBodyText small extraClasses="total">
              Vendor pay upfront
            </AgxBodyText>
            <AgxBodyText small extraClasses="inclusions-rowText">
              {formatCurrency(
                marketingPackage.marketingContributions?.vendorUpfrontPayment
              )}
            </AgxBodyText>
          </AgxRow>
        )}
      {!!marketingPackage.marketingContributions
        ?.agentContributionNoReimbursement && (
        <AgxRow fill extraClasses="total-row" spaceBetween>
          <AgxBodyText small extraClasses="total">
            Agent contribution (no reimbursement)
          </AgxBodyText>
          <AgxBodyText small extraClasses="inclusions-rowText">
            {formatCurrency(
              marketingPackage.marketingContributions
                ?.agentContributionNoReimbursement
            )}
          </AgxBodyText>
        </AgxRow>
      )}
      {!!marketingPackage.marketingContributions
        ?.agentContributionToBeReimbursed && (
        <AgxRow fill extraClasses="total-row" spaceBetween>
          <AgxBodyText small extraClasses="total">
            Agent contribution (to be reimbursed)
          </AgxBodyText>
          <AgxBodyText small extraClasses="inclusions-rowText">
            {formatCurrency(
              marketingPackage.marketingContributions
                ?.agentContributionToBeReimbursed
            )}
          </AgxBodyText>
        </AgxRow>
      )}
      <AgxRow fill extraClasses="total-row" spaceBetween>
        <AgxBodyText small extraClasses="total">
          Total
        </AgxBodyText>
        <AgxBodyText small extraClasses="inclusions-rowText totalPrice">
          {formatCurrency(marketingPackageTotal)}
        </AgxBodyText>
      </AgxRow>
    </div>
  );

  return (
    <>
      <FormDetailsHeader
        id={'marketing-package-complete-header'}
        formName="Marketing"
        detail={lastUpdatedOn}
        onBack={onBack}
      />
      <AgxColumn
        centered
        veryLargeGap
        extraClasses="marketingPackageCompleteDetails"
      >
        <AgxHeader size={2} centered>
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxHeader>
        <AgxBodyText large centered>
          {marketingPackage.packageTemplateName}
        </AgxBodyText>
      </AgxColumn>
      {marketingPackage?.displayStatus && (
        <AgxColumn>{marketingPaymentStatusComponent}</AgxColumn>
      )}
      {marketingPackage?.inclusions && (
        <AgxColumn>{marketingInclusionsComponent}</AgxColumn>
      )}
      {marketingPackage?.addons && (
        <AgxColumn>{marketingAddonsComponent}</AgxColumn>
      )}
      {!marketingPackage?.addons?.length &&
        !marketingPackage?.inclusions?.length && (
          <AgxColumn>{noMarketingInclusionsComponent}</AgxColumn>
        )}
      <AgxColumn>{marketingTotalComponent}</AgxColumn>

      <AgxRow fill justifyCenter largeGap>
        <AgxButton
          text="Download"
          hollow
          large={!isMobile}
          medium={isMobile}
          onClick={() => downloadDocument()}
        />
      </AgxRow>
    </>
  );
};

export default MarketingPackageCompletePanel;
